import React, { useState } from 'react';
import { MoreHorizontal, Check, X, Trash2, Eye, MessageCircle, Shield, Edit, DollarSign, Loader2, AlertCircle, Calendar, Users, UserPlus, Search } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAllOrdersAdmin, updateOrderStatus, cancelOrder, extendOrderDelivery, updateOrderHellocians, remindReview, requestAdditionalPayment } from '../../services/order.service';
import { getActiveHellocians } from '../../services/hellocian.service';
import { useAlert } from '../../context/AlertContext';

const OrdersTable = () => {
    const queryClient = useQueryClient();
    const setAlert = useAlert();
    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState('');
    const [showHeaderDropdown, setShowHeaderDropdown] = useState(false);
    const [showRowDropdown, setShowRowDropdown] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [newStatus, setNewStatus] = useState('');
    const [cancelReason, setCancelReason] = useState('');
    const [extendDate, setExtendDate] = useState('');
    const [extendReason, setExtendReason] = useState('');
    const [requestMoreAmount, setRequestMoreAmount] = useState('');
    const [requestMoreReason, setRequestMoreReason] = useState('');
    const [selectedHellocianIds, setSelectedHellocianIds] = useState([]);
    const [searchHellocian, setSearchHellocian] = useState('');

    const openModal = (action, order) => {
        setSelectedOrder({ ...order, action });
        if (action === 'status') {
            setNewStatus(order.status);
        }
        if (action === 'extend') {
            const currentOrderDate = new Date(order.deliveryDate);
            // Default to current delivery date + 3 days
            const nextDate = new Date(currentOrderDate);
            nextDate.setDate(nextDate.getDate() + 3);
            setExtendDate(nextDate.toISOString().split('T')[0]);
        }
        if (action === 'hellocians') {
            setSelectedHellocianIds(order.hellocians?.map(h => h._id) || []);
        }
        setShowModal(true);
        setShowRowDropdown(null);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedOrder(null);
        setNewStatus('');
        setCancelReason('');
        setExtendDate('');
        setExtendReason('');
        setRequestMoreAmount('');
        setRequestMoreReason('');
    };

    const { data, isLoading, error } = useQuery({
        queryKey: ['adminOrders', { page }],
        queryFn: () => getAllOrdersAdmin({ page, limit: 100 }),
        keepPreviousData: true
    });

    const updateStatusMutation = useMutation({
        mutationFn: ({ orderId, status }) => updateOrderStatus(orderId, status),
        onSuccess: () => {
            queryClient.invalidateQueries(['adminOrders']);
            setAlert({ type: 'success', message: 'Order status updated successfully' });
            closeModal();
        },
        onError: (err) => {
            setAlert({ type: 'error', message: err.response?.data?.error || 'Failed to update status' });
        }
    });

    const cancelOrderMutation = useMutation({
        mutationFn: ({ orderId, reason }) => cancelOrder(orderId, reason),
        onSuccess: () => {
            queryClient.invalidateQueries(['adminOrders']);
            setAlert({ type: 'success', message: 'Order cancelled successfully' });
            closeModal();
        },
        onError: (err) => {
            setAlert({ type: 'error', message: err.response?.data?.error || 'Failed to cancel order' });
        }
    });

    const extendOrderMutation = useMutation({
        mutationFn: ({ orderId, newDeliveryDate, reason }) => extendOrderDelivery(orderId, newDeliveryDate, reason),
        onSuccess: () => {
            queryClient.invalidateQueries(['adminOrders']);
            setAlert({ type: 'success', message: 'Order delivery date extended successfully' });
            closeModal();
        },
        onError: (err) => {
            setAlert({ type: 'error', message: err.response?.data?.error || 'Failed to extend delivery' });
        }
    });

    const updateHellociansMutation = useMutation({
        mutationFn: ({ orderId, hellocianIds }) => updateOrderHellocians(orderId, hellocianIds),
        onSuccess: () => {
            queryClient.invalidateQueries(['adminOrders']);
            setAlert({ type: 'success', message: 'Hellocians updated successfully' });
            closeModal();
        },
        onError: (err) => {
            setAlert({ type: 'error', message: err.response?.data?.error || 'Failed to update Hellocians' });
        }
    });

    const remindReviewMutation = useMutation({
        mutationFn: (orderId) => remindReview(orderId),
        onSuccess: () => {
            setAlert({ type: 'success', message: 'Review reminder sent successfully' });
            setShowRowDropdown(null);
        },
        onError: (err) => {
            setAlert({ type: 'error', message: err.response?.data?.error || 'Failed to send reminder' });
        }
    });

    const requestMoreMutation = useMutation({
        mutationFn: ({ orderId, amount, description }) => requestAdditionalPayment(orderId, amount, description),
        onSuccess: () => {
            queryClient.invalidateQueries(['adminOrders']);
            setAlert({ type: 'success', message: 'Additional payment requested successfully' });
            closeModal();
        },
        onError: (err) => {
            setAlert({ type: 'error', message: err.response?.data?.error || 'Failed to request payment' });
        }
    });

    const { data: activeHellocianData } = useQuery({
        queryKey: ['activeHellocians'],
        queryFn: getActiveHellocians,
        enabled: !!(selectedOrder && selectedOrder.action === 'hellocians')
    });

    const handleUpdateStatus = () => {
        if (!selectedOrder || !newStatus) return;
        updateStatusMutation.mutate({ orderId: selectedOrder._id, status: newStatus });
    };

    const handleCancelOrder = () => {
        if (!selectedOrder) return;
        cancelOrderMutation.mutate({ orderId: selectedOrder._id, reason: cancelReason });
    };

    const handleExtendOrder = () => {
        if (!selectedOrder || !extendDate) return;
        extendOrderMutation.mutate({ 
            orderId: selectedOrder._id, 
            newDeliveryDate: extendDate, 
            reason: extendReason 
        });
    };

    const handleUpdateHellocians = () => {
        if (!selectedOrder) return;
        updateHellociansMutation.mutate({
            orderId: selectedOrder._id,
            hellocianIds: selectedHellocianIds
        });
    };

    const handleRequestMore = () => {
        if (!selectedOrder || !requestMoreAmount || !requestMoreReason) return;
        requestMoreMutation.mutate({
            orderId: selectedOrder._id,
            amount: Number(requestMoreAmount),
            description: requestMoreReason
        });
    };

    const StatusBadge = ({ status }) => {
        const getStatusColors = () => {
            switch (status) {
                case 'completed':
                    return 'border-[1px] border-[#1FA860] text-[#1FA860] bg-[#1FA860]/5';
                case 'in-progress':
                    return 'border-[1px] border-[#6366F1] text-[#6366F1] bg-[#6366F1]/5';
                case 'pending':
                    return 'border-[1px] border-[#FFA500] text-[#FFA500] bg-[#FFA500]/5';
                case 'cancelled':
                    return 'border-[1px] border-[#FF4444] text-[#FF4444] bg-[#FF4444]/5';
                case 'disputed':
                    return 'border-[1px] border-[#d32f2f] text-[#d32f2f] bg-[#d32f2f]/5';
                default:
                    return 'border-[1px] border-[#AFAFAF] text-[#AFAFAF] bg-[#AFAFAF]/5';
            }
        };

        return (
            <span className={`px-3 py-1 rounded text-sm font-medium capitalize ${getStatusColors()}`}>
                {status}
            </span>
        );
    };

    if (isLoading) {
        return (
            <div className="bg-white dark:bg-[#141414] rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-12 flex flex-col items-center justify-center">
                <Loader2 className="w-12 h-12 text-[#4B718F] animate-spin mb-4" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white dark:bg-[#141414] rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-12 flex flex-col items-center justify-center text-center">
                <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Failed to load projects</h3>
                <p className="text-gray-500 dark:text-gray-400 mb-6">{error.response?.data?.error || 'Something went wrong while fetching data.'}</p>
                <button 
                    onClick={() => queryClient.invalidateQueries(['adminOrders'])}
                    className="px-6 py-2 bg-[#4B718F] text-white rounded-lg hover:bg-[#3a5a75] transition-colors"
                >
                    Try Again
                </button>
            </div>
        );
    }

    const orders = data?.orders || [];
    const stats = data?.stats || [];

    const filteredOrders = statusFilter 
        ? orders.filter(order => order.status === statusFilter)
        : orders;

    if (orders.length === 0 && !statusFilter) {
        return (
            <div className="bg-white dark:bg-[#141414] rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-8">
                <div className="text-center flex flex-col gap-3">
                    <div className="text-6xl mb-4">📋</div>
                    <p className="text-gray-500 dark:text-gray-400">There are no orders here at the moment, please check back later. Thank You!</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Stats Summary */}
            <div className="grid grid-cols-3 md:grid-cols-2 lg:grid-cols-5 gap-4">
                {['pending', 'in-progress', 'completed', 'cancelled', 'disputed'].map((s) => {
                    const stat = stats.find(item => item._id === s);
                    return (
                        <div key={s} className="bg-white dark:bg-[#1d2026] p-2 md:p-4 rounded-lg shadow-sm border border-gray-200 dark:border-[#32353c]">
                            <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 capitalize">{s.replace('-', ' ')}</p>
                            <h3 className="text-xl md:text-2xl font-bold text-[#174568] dark:text-blue-400">{stat?.count || 0}</h3>
                        </div>
                    );
                })}
            </div>

            <div className="bg-white dark:bg-[#141414] rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 font-['Poppins']">
                {/* Header & Filters */}
                <div className="px-6 py-4 border-b border-gray-200 dark:border-[#32353c] flex flex-row sm:flex-row items-center justify-between gap-4">
                    <h2 className="text-lg md:text-xl font-medium md:font-semibold text-[#4B718F] dark:text-blue-400">All Projects</h2>
                    
                    <div className="flex items-center gap-3">
                        <select 
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="text-xs md:text-sm border border-gray-300 dark:border-[#32353c] dark:bg-[#10131a] dark:text-white rounded-lg px-3 py-2 focus:ring-1 focus:ring-[#4B718F] outline-none"
                        >
                            <option value="">All Statuses</option>
                            <option value="pending">Pending</option>
                            <option value="in-progress">In Progress</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                            <option value="disputed">Disputed</option>
                        </select>
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 dark:bg-[#1d2026]/60">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[#A3AED0] uppercase tracking-wider">Order ID</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[#A3AED0] uppercase tracking-wider">Project / Gig</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[#A3AED0] uppercase tracking-wider">Client</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[#A3AED0] uppercase tracking-wider">Hellocians</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[#A3AED0] uppercase tracking-wider">Amount</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[#A3AED0] uppercase tracking-wider">Date</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[#A3AED0] uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-[#A3AED0] uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-[#141414] divide-y divide-gray-200 dark:divide-gray-800">
                            {filteredOrders.map((order) => (
                                <tr key={order._id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 font-mono">
                                        #{order._id.substring(order._id.length - 6).toUpperCase()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-xs">{order.title}</div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 capitalize">{order.pricingPackage}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            {order.clientId?.profilePicture && (
                                                <img 
                                                    src={order.clientId.profilePicture} 
                                                    alt="" 
                                                    className="w-8 h-8 rounded-full mr-2 object-cover"
                                                />
                                            )}
                                            <div>
                                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                    {order.clientId?.firstName} {order.clientId?.lastName}
                                                </div>
                                                <div className="text-xs text-gray-500 dark:text-gray-400">{order.clientId?.email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900 dark:text-white font-medium">
                                            {(!order.hellocians || order.hellocians.length === 0) ? (
                                                <span className="text-gray-400 italic">Unassigned</span>
                                            ) : (
                                                <>
                                                    {order.hellocians.slice(0, 2).map(h => h.username || h.firstName).join(', ')}
                                                    {order.hellocians.length > 2 && (
                                                        <span className="ml-1 text-[#4B718F] font-bold">+{order.hellocians.length - 2}</span>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white font-semibold">
                                        ${order.price}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                        {new Date(order.createdAt).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <StatusBadge status={order.status} />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <div className="relative flex justify-end">
                                            <button
                                                onClick={() => setShowRowDropdown(showRowDropdown === order._id ? null : order._id)}
                                                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                                            >
                                                <MoreHorizontal className="w-5 h-5 text-gray-400" />
                                            </button>

                                            {showRowDropdown === order._id && (
                                                <div className="absolute right-0 mt-8 w-48 bg-white dark:bg-[#1d2026] border border-gray-200 dark:border-[#32353c] rounded-lg shadow-lg z-20 overflow-hidden">
                                                    <button
                                                        onClick={() => openModal('status', order)}
                                                        className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 text-sm"
                                                    >
                                                        <Edit className="w-4 h-4 text-gray-500" />
                                                        Update Status
                                                    </button>
                                                    <button
                                                        onClick={() => openModal('extend', order)}
                                                        className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400"
                                                    >
                                                        <Calendar className="w-4 h-4" />
                                                        Extend Delivery
                                                    </button>
                                                    <button
                                                        onClick={() => openModal('cancel', order)}
                                                        className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 text-sm text-orange-600 dark:text-orange-400"
                                                    >
                                                        <Shield className="w-4 h-4" />
                                                        Cancel Order
                                                    </button>
                                                    <button
                                                        onClick={() => openModal('hellocians', order)}
                                                        className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 text-sm text-[#174568] dark:text-blue-300"
                                                    >
                                                        <UserPlus className="w-4 h-4" />
                                                        Manage Hellocians
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            window.location.href = `/clients/messages/${order.chatId?._id || order.chatId}`;
                                                        }}
                                                        className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 text-sm dark:text-gray-300"
                                                    >
                                                        <MessageCircle className="w-4 h-4 text-gray-500" />
                                                        Go to Chat
                                                    </button>
                                                    {['pending', 'in-progress'].includes(order.status) && (
                                                        <button
                                                            onClick={() => openModal('request-more', order)}
                                                            className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 text-sm text-green-600 dark:text-green-400"
                                                        >
                                                            <DollarSign className="w-4 h-4 text-green-600" />
                                                            Request More
                                                        </button>
                                                    )}
                                                    {order.status === 'completed' && !order.isReviewed && (
                                                        <button
                                                            onClick={() => remindReviewMutation.mutate(order._id)}
                                                            disabled={remindReviewMutation.isPending}
                                                            className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 text-sm text-indigo-600 dark:text-indigo-400"
                                                        >
                                                            {remindReviewMutation.isPending ? (
                                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                            ) : (
                                                                <AlertCircle className="w-4 h-4" />
                                                            )}
                                                            Remind for Review
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {data?.pagination && data.pagination.pages > 1 && (
                    <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200 dark:border-[#32353c]">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            Showing page {page} of {data.pagination.pages}
                        </p>
                        <div className="flex gap-2">
                            <button 
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                                className="px-3 py-1 border border-gray-300 dark:border-gray-600 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                            >
                                Previous
                            </button>
                            <button 
                                onClick={() => setPage(p => Math.min(data.pagination.pages, p + 1))}
                                disabled={page === data.pagination.pages}
                                className="px-3 py-1 border border-gray-300 dark:border-gray-600 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal */}
            {showModal && selectedOrder && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4 backdrop-blur-sm">
                    <div className="bg-white dark:bg-[#0b0e14] rounded-xl shadow-xl max-w-md w-full overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
                            <h3 className="text-lg font-bold text-[#174568] dark:text-blue-400">
                                {selectedOrder.action === 'status' ? 'Update Order Status' : 
                                 selectedOrder.action === 'extend' ? 'Extend Delivery Time' : 
                                 selectedOrder.action === 'hellocians' ? 'Manage Hellocians' : 
                                 selectedOrder.action === 'request-more' ? 'Request Additional Payment' : 'Cancel Order'}
                            </h3>
                            <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="p-6">
                            {selectedOrder.action === 'status' ? (
                                <div className="space-y-4">
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        Update the status for order <strong>#{selectedOrder._id.substring(selectedOrder._id.length - 6).toUpperCase()}</strong>
                                    </p>
                                    <div className="space-y-1">
                                        <label className="text-xs font-semibold text-gray-500 dark:text-gray-500 uppercase">Select New Status</label>
                                        <select 
                                            value={newStatus}
                                            onChange={(e) => setNewStatus(e.target.value)}
                                            className="w-full p-3 border border-gray-300 dark:border-[#32353c] dark:bg-[#10131a] dark:text-white rounded-lg focus:ring-2 focus:ring-[#4B718F] outline-none"
                                        >
                                            <option value="pending">Pending</option>
                                            <option value="in-progress">In Progress</option>
                                            <option value="completed">Completed</option>
                                            <option value="cancelled">Cancelled</option>
                                            <option value="disputed">Disputed</option>
                                        </select>
                                    </div>
                                </div>
                            ) : selectedOrder.action === 'extend' ? (
                                <div className="space-y-4">
                                     <p className="text-sm text-gray-600 dark:text-gray-400">
                                        Extend delivery date for <strong>#{selectedOrder._id.substring(selectedOrder._id.length - 6).toUpperCase()}</strong>
                                    </p>
                                    <div className="space-y-1">
                                        <label className="text-xs font-semibold text-gray-500 dark:text-gray-500 uppercase">Current Deadline</label>
                                        <div className="p-3 bg-gray-50 dark:bg-[#1d2026]/50 border border-gray-200 dark:border-[#32353c] rounded-lg text-sm text-gray-700 dark:text-gray-300">
                                            {new Date(selectedOrder.deliveryDate).toLocaleDateString()} at {new Date(selectedOrder.deliveryDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </div>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-semibold text-gray-500 dark:text-gray-500 uppercase">New Delivery Date</label>
                                        <input
                                            type="date"
                                            value={extendDate}
                                            onChange={(e) => setExtendDate(e.target.value)}
                                            min={new Date(selectedOrder.deliveryDate).toISOString().split('T')[0]}
                                            className="w-full p-3 border border-gray-300 dark:border-[#32353c] dark:bg-[#1d2026] dark:text-white rounded-lg focus:ring-2 focus:ring-[#4B718F] outline-none"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-semibold text-gray-500 dark:text-gray-500 uppercase">Extension Reason (Optional)</label>
                                        <textarea
                                            value={extendReason}
                                            onChange={(e) => setExtendReason(e.target.value)}
                                            placeholder="Why is the project being extended?"
                                            className="w-full p-3 border border-gray-300 dark:border-[#32353c] dark:bg-inherit dark:text-white rounded-lg focus:ring-2 focus:ring-[#4B718F] outline-none resize-none"
                                            rows="3"
                                        />
                                    </div>
                                </div>
                            ) : selectedOrder.action === 'cancel' ? (
                                <div className="space-y-4">
                                    <div className="p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-100 dark:border-orange-800 rounded-lg flex gap-3">
                                        <Shield className="text-orange-500 shrink-0" />
                                        <p className="text-sm text-orange-800 dark:text-orange-300">
                                            Are you sure you want to cancel this order? This will notify the client.
                                        </p>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-semibold text-gray-500 dark:text-gray-500 uppercase">Reason (Optional)</label>
                                        <textarea
                                            value={cancelReason}
                                            onChange={(e) => setCancelReason(e.target.value)}
                                            placeholder="Enter reason for cancellation..."
                                            className="w-full p-3 border border-gray-300 dark:border-[#32353c] dark:bg-inherit dark:text-white rounded-lg focus:ring-2 focus:ring-[#4B718F] outline-none resize-none"
                                            rows="3"
                                        />
                                    </div>
                                </div>
                            ) : selectedOrder.action === 'request-more' ? (
                                <div className="space-y-4">
                                    {selectedOrder.additionalPayments && selectedOrder.additionalPayments.length > 0 && (
                                        <div className="space-y-2 mb-4">
                                            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Payment History</h4>
                                            <div className="max-h-32 overflow-y-auto space-y-2">
                                                {selectedOrder.additionalPayments.map((p, idx) => (
                                                    <div key={idx} className="flex justify-between items-center p-2 bg-gray-50 dark:bg-[#1d2026]/50 border border-gray-100 dark:border-gray-800 rounded text-sm">
                                                        <div>
                                                            <span className="font-medium text-gray-800 dark:text-gray-200">${p.amount}</span>
                                                            <span className="text-gray-500 dark:text-gray-400 text-xs ml-2">- {p.description}</span>
                                                        </div>
                                                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium capitalize ${p.status === 'paid' ? 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400' : 'bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-400'}`}>
                                                            {p.status}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800 rounded-lg flex gap-3">
                                        <DollarSign className="text-green-500 shrink-0" />
                                        <p className="text-sm text-green-800 dark:text-green-300">
                                            Request an additional payment from the client for order <strong>#{selectedOrder._id.substring(selectedOrder._id.length - 6).toUpperCase()}</strong>.
                                        </p>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-semibold text-gray-500 dark:text-gray-500 uppercase">Amount ($)</label>
                                        <input
                                            type="number"
                                            value={requestMoreAmount}
                                            onChange={(e) => setRequestMoreAmount(e.target.value)}
                                            placeholder="Enter amount..."
                                            min="1"
                                            className="w-full p-3 border border-gray-300 dark:border-[#32353c] dark:bg-[#1d2026] dark:text-white rounded-lg focus:ring-2 focus:ring-[#4B718F] outline-none"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-semibold text-gray-500 dark:text-gray-500 uppercase">Reason</label>
                                        <textarea
                                            value={requestMoreReason}
                                            onChange={(e) => setRequestMoreReason(e.target.value)}
                                            placeholder="Reason for requesting more payment..."
                                            className="w-full p-3 border border-gray-300 dark:border-[#32353c] dark:bg-inherit dark:text-white rounded-lg focus:ring-2 focus:ring-[#4B718F] outline-none resize-none"
                                            rows="3"
                                        />
                                    </div>
                                </div>
                            ) : selectedOrder.action === 'hellocians' ? (
                                <div className="space-y-4">
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        Assign or remove Hellocians for <strong>#{selectedOrder._id.substring(selectedOrder._id.length - 6).toUpperCase()}</strong>
                                    </p>
                                    
                                    <div className="relative mb-2">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                                        <input 
                                            type="text"
                                            placeholder="Search active Hellocians..."
                                            className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-[#32353c] dark:bg-[#1d2026] dark:text-white rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#4B718F]"
                                            value={searchHellocian}
                                            onChange={(e) => setSearchHellocian(e.target.value)}
                                        />
                                    </div>

                                    <div className="max-h-60 overflow-y-auto space-y-2 pr-2 border border-gray-100 dark:border-gray-800 rounded-lg p-2 bg-gray-50/30 dark:bg-[#1d2026]/30">
                                        {(activeHellocianData?.hellocians || [])
                                            .filter(h => `${h.firstName} ${h.lastName}`.toLowerCase().includes(searchHellocian.toLowerCase()))
                                            .map(h => (
                                            <div 
                                                key={h._id}
                                                className={`flex items-center justify-between p-3 rounded-lg border transition-all cursor-pointer ${
                                                    selectedHellocianIds.includes(h._id) 
                                                    ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-900 ring-1 ring-blue-100 dark:ring-blue-900/40' 
                                                    : 'bg-white dark:bg-[#1d2026] border-gray-200 dark:border-[#32353c] hover:border-gray-300 dark:hover:border-gray-600'
                                                }`}
                                                onClick={() => {
                                                    if (selectedHellocianIds.includes(h._id)) {
                                                        setSelectedHellocianIds(prev => prev.filter(id => id !== h._id));
                                                    } else {
                                                        setSelectedHellocianIds(prev => [...prev, h._id]);
                                                    }
                                                }}
                                            >
                                                <div className="flex items-center gap-3">
                                                    {h.profilePicture ? (
                                                        <img src={h.profilePicture} alt="" className="w-8 h-8 rounded-full object-cover border border-gray-100 dark:border-[#32353c]" />
                                                    ) : (
                                                        <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-[#174568] dark:text-blue-400 text-xs font-bold uppercase">
                                                            {h.firstName[0]}{h.lastName[0]}
                                                        </div>
                                                    )}
                                                    <div>
                                                        <p className="text-sm font-semibold text-gray-800 dark:text-white">{h.firstName} {h.lastName}</p>
                                                        <p className="text-[10px] text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wider">{h.username || 'Hellocian'}</p>
                                                    </div>
                                                </div>
                                                <div className={`w-5 h-5 rounded flex items-center justify-center border transition-colors ${
                                                    selectedHellocianIds.includes(h._id)
                                                    ? 'bg-[#174568] border-[#174568]'
                                                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-[#1d2026]'
                                                }`}>
                                                    {selectedHellocianIds.includes(h._id) && <Check className="w-3.5 h-3.5 text-white" />}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <p className="text-[10px] text-gray-500 italic">Only activated Hellocians appear in this list. Changes take effect immediately upon confirmation.</p>
                                </div>
                            ) : null}

                            <div className="mt-8 flex gap-3">
                                <button
                                    onClick={closeModal}
                                    className="flex-1 py-3 px-4 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                                >
                                    Go Back
                                </button>
                                <button
                                    onClick={selectedOrder.action === 'status' ? handleUpdateStatus : 
                                             selectedOrder.action === 'extend' ? handleExtendOrder : 
                                             selectedOrder.action === 'request-more' ? handleRequestMore :
                                             selectedOrder.action === 'hellocians' ? handleUpdateHellocians : handleCancelOrder}
                                    disabled={updateStatusMutation.isPending || cancelOrderMutation.isPending || extendOrderMutation.isPending || updateHellociansMutation.isPending || requestMoreMutation.isPending}
                                    className={`flex-1 py-3 px-4 text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 ${
                                        selectedOrder.action === 'cancel' ? 'bg-orange-600 hover:bg-orange-700' : 
                                        selectedOrder.action === 'request-more' ? 'bg-green-600 hover:bg-green-700' : 
                                        'bg-[#4B718F] hover:bg-[#3a5a75]'
                                    }`}
                                >
                                    {(updateStatusMutation.isPending || cancelOrderMutation.isPending || extendOrderMutation.isPending || requestMoreMutation.isPending) && <Loader2 className="w-4 h-4 animate-spin" />}
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Backdrop for dropdowns */}
            {showRowDropdown && (
                <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setShowRowDropdown(null)}
                />
            )}
        </div>
    );
};

export default OrdersTable;